export type EnhancementMode = 'original' | 'equalize' | 'clahe';
export type EnhancementSettings = {
  mode: EnhancementMode; clipLimit: number; tiles: number; gamma: number; invert: boolean;
};
export const defaultEnhancement = (): EnhancementSettings => ({
  mode: 'original', clipLimit: 2, tiles: 8, gamma: 1, invert: false,
});

/** Copy one native IJK plane; no resampling and no writes to the source. */
export function extractPlane(values: ArrayLike<number>, dimensions: number[], axis: number, slice: number) {
  const dims = [...dimensions]; dims[axis] = 1;
  const plane = new Float32Array(dims[0] * dims[1] * dims[2]);
  let out = 0;
  for (let k = 0; k < dims[2]; k++) for (let j = 0; j < dims[1]; j++) for (let i = 0; i < dims[0]; i++) {
    const ijk = [i, j, k]; ijk[axis] = slice;
    plane[out++] = values[ijk[0] + dimensions[0] * (ijk[1] + dimensions[1] * ijk[2])];
  }
  const inPlane = [0, 1, 2].filter(a => a !== axis);
  return { values: plane, dimensions: dims, width: dims[inPlane[0]], height: dims[inPlane[1]] };
}

/** Window to display bytes first. The original scalar values remain untouched. */
export function windowBytes(values: ArrayLike<number>, width: number, level: number) {
  const span = Math.max(Number.isFinite(width) ? width : 1, 1e-6);
  const low = (Number.isFinite(level) ? level : 0) - span / 2;
  return Uint8Array.from(values, value => Number.isFinite(value) ? Math.round(Math.max(0, Math.min(1, (value - low) / span)) * 255) : 0);
}

export function equalize(bytes: Uint8Array) {
  const hist = new Uint32Array(256); for (const value of bytes) hist[value]++;
  const first = hist.findIndex(count => count !== 0);
  if (first < 0 || hist[first] === bytes.length) return bytes.slice();
  const lut = new Uint8Array(256); let cumulative = 0;
  for (let i = 0; i < 256; i++) { cumulative += hist[i]; lut[i] = Math.max(0, Math.round((cumulative - hist[first]) * 255 / (bytes.length - hist[first]))); }
  return Uint8Array.from(bytes, value => lut[value]);
}

// CLAHE uses clipped tile histograms, uniform redistribution and interpolation
// between tile centers. Unequal edge tiles retain their actual pixel counts.
// Algorithm reference: https://docs.opencv.org/4.x/d5/daf/tutorial_py_histogram_equalization.html
export function clahe(bytes: Uint8Array, width: number, height: number, tileCount: number, clipLimit: number) {
  const nx = Math.min(width, Math.max(1, Math.round(tileCount))), ny = Math.min(height, Math.max(1, Math.round(tileCount)));
  const xEdges = Array.from({ length: nx + 1 }, (_, i) => Math.floor(i * width / nx));
  const yEdges = Array.from({ length: ny + 1 }, (_, i) => Math.floor(i * height / ny));
  const luts: Uint8Array[] = [];
  for (let ty = 0; ty < ny; ty++) for (let tx = 0; tx < nx; tx++) {
    const hist = new Uint32Array(256); const count = (xEdges[tx + 1] - xEdges[tx]) * (yEdges[ty + 1] - yEdges[ty]);
    for (let y = yEdges[ty]; y < yEdges[ty + 1]; y++) for (let x = xEdges[tx]; x < xEdges[tx + 1]; x++) hist[bytes[y * width + x]]++;
    const cap = Math.max(1, Math.floor(Math.max(1, clipLimit) * count / 256)); let excess = 0;
    for (let b = 0; b < 256; b++) { excess += Math.max(0, hist[b] - cap); hist[b] = Math.min(hist[b], cap); }
    const batch = Math.floor(excess / 256); const remainder = excess % 256;
    for (let b = 0; b < 256; b++) hist[b] += batch;
    for (let b = 0; b < remainder; b++) hist[Math.floor(b * 256 / remainder)]++;
    const lut = new Uint8Array(256); let cumulative = 0;
    for (let b = 0; b < 256; b++) { cumulative += hist[b]; lut[b] = Math.round(cumulative * 255 / count); }
    luts.push(lut);
  }
  function weights(edges: number[], size: number) {
    const centers = edges.slice(0, -1).map((edge, i) => (edge + edges[i + 1] - 1) / 2);
    return Array.from({ length: size }, (_, pixel) => {
      let right = centers.findIndex(center => center >= pixel);
      if (right <= 0) { const i = right === 0 ? 0 : centers.length - 1; return [i, i, 0]; }
      return [right - 1, right, (pixel - centers[right - 1]) / (centers[right] - centers[right - 1])];
    });
  }
  const xs = weights(xEdges, width), ys = weights(yEdges, height); const out = new Uint8Array(bytes.length);
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const [x0, x1, wx] = xs[x], [y0, y1, wy] = ys[y], b = bytes[y * width + x];
    const top = luts[y0 * nx + x0][b] * (1 - wx) + luts[y0 * nx + x1][b] * wx;
    const bottom = luts[y1 * nx + x0][b] * (1 - wx) + luts[y1 * nx + x1][b] * wx;
    out[y * width + x] = Math.round(top * (1 - wy) + bottom * wy);
  }
  return out;
}

export function enhancePlane(values: Float32Array, width: number, height: number, windowWidth: number, level: number, settings: EnhancementSettings) {
  let pixels = windowBytes(values, windowWidth, level);
  if (settings.mode === 'equalize') pixels = equalize(pixels);
  if (settings.mode === 'clahe') pixels = clahe(pixels, width, height, settings.tiles, settings.clipLimit);
  const gamma = Math.max(.3, Math.min(3, settings.gamma));
  return Uint8Array.from(pixels, value => { const corrected = Math.round(255 * (value / 255) ** (1 / gamma)); return settings.invert ? 255 - corrected : corrected; });
}
